import { User } from '../models/User';
import { logUserActivity } from './adminActivityDatabase.ts';
import { getTrackingInfo } from '../utils/trackingUtils.ts';
import { db } from '../config/firebase.ts';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  Timestamp,
  writeBatch,
  serverTimestamp,
  DocumentData,
  QueryDocumentSnapshot,
  FirestoreDataConverter
} from 'firebase/firestore';
import { hashPassword, verifyPassword } from '../utils/authUtils.ts';

// Additional interfaces for enhanced functionality
export interface BaseUser extends Omit<User, 'id'> {
  id?: string;
}

// Helper function to get display name
export const getDisplayName = (user: User | null | undefined): string => {
  if (!user) return 'Unknown User';
  return user.fullName || 'Unknown User';
};

export interface ProfilePictureProcessingOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  format?: 'jpeg' | 'png' | 'webp';
}

// List of countries for dropdown selection
export const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina', 'Armenia', 'Australia',
  'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium',
  'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei',
  'Bulgaria', 'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon', 'Canada', 'Cape Verde',
  'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
  'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
  'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia',
  'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada',
  'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India',
  'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan',
  'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia',
  'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives',
  'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova',
  'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal',
  'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway',
  'Oman', 'Pakistan', 'Palau', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland',
  'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia',
  'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia',
  'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands',
  'Somalia', 'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname',
  'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo',
  'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine',
  'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu',
  'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
];

// Default user data
const DEFAULT_USERS: User[] = [
  {
    id: '1',
    fullName: 'Håvar Bauck',
    password: 'test123',
    isAdmin: true,
    isSuperAdmin: true,
    email: 'havar@bauck.com',
    phoneNumber: '+47 907 67 167',
    country: 'Norway',
    notificationCount: 2,
    profilePictureUrl: 'https://media.licdn.com/dms/image/v2/C4D03AQE7ZI0MPHgNcw/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1622562434166?e=1737590400&v=beta&t=Sx0NX8Sp5X8-MUKJxQG-oSUOuKGNgYT3_Aa9f_yHBII'
  },
  {
    id: '2',
    fullName: 'Endre Opdal',
    password: 'test123',
    isAdmin: true,
    isSuperAdmin: true,
    email: 'endre@example.com',
    phoneNumber: '+47 987 65 432',
    country: 'Norway',
    notificationCount: 3
  },
  {
    id: '3',
    fullName: 'Test User',
    password: 'test123',
    isAdmin: false,
    isSuperAdmin: false,
    email: 'user@example.com',
    phoneNumber: '+1 555-123-4567',
    country: 'United States',
    notificationCount: 1
  },
  {
    id: '4',
    fullName: 'Hotel Paradise Corp',
    password: 'test123',
    isAdmin: true,
    isSuperAdmin: false,
    email: 'contact@hotelparadise.com',
    phoneNumber: '+1 555-987-6543',
    country: 'United States',
    notificationCount: 0
  },
  {
    id: '5',
    fullName: 'Test Admin 2',
    password: 'test123',
    isAdmin: true,
    isSuperAdmin: false,
    email: 'admin2@example.com',
    phoneNumber: '+1 555-222-3333',
    country: 'Canada',
    notificationCount: 0
  },
  {
    id: '6',
    fullName: 'Sunshine Hotels Ltd',
    password: 'test123',
    isAdmin: false,
    isSuperAdmin: false,
    email: 'info@sunshinehotels.com',
    phoneNumber: '+1 555-444-5555',
    country: 'United Kingdom',
    notificationCount: 0
  },
  {
    id: '7',
    fullName: 'Test User 3',
    password: 'test123',
    isAdmin: false,
    isSuperAdmin: false,
    email: 'user3@example.com',
    phoneNumber: '+1 555-666-7777',
    country: 'Germany',
    notificationCount: 0
  }
];

// Create a Firestore converter for User type
const userConverter: FirestoreDataConverter<User> = {
  toFirestore: (user: User): DocumentData => {
    const { id, ...rest } = user;
    return rest;
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot): User => {
    const data = snapshot.data();
    return {
      ...data,
      id: snapshot.id
    } as User;
  }
};

// Initialize users from Firestore or defaults
let USERS: User[] = [];

export const initializeUserDatabase = async (): Promise<void> => {
  try {
    const usersRef = collection(db, 'users').withConverter(userConverter);
    const usersSnapshot = await getDocs(usersRef);
    
    if (usersSnapshot.empty) {
      // Initialize with defaults if empty
      const batch = writeBatch(db);
      DEFAULT_USERS.forEach(user => {
        const docRef = doc(db, 'users', user.id);
        batch.set(docRef, user);
      });
      await batch.commit();
      USERS = [...DEFAULT_USERS];
    } else {
      USERS = usersSnapshot.docs.map(doc => doc.data());
    }
  } catch (error) {
    console.error('Failed to initialize user database:', error);
    // Fallback to defaults
    USERS = [...DEFAULT_USERS];
  }
};

// Helper function to enhance users with computed properties
const enhanceUser = (user: User): User => ({
  ...user,
  hasProfilePicture: hasProfilePicture(user.id)
});

// User database functions
export const getAllUsers = async (): Promise<User[]> => {
  try {
    const usersQuery = query(collection(db, 'users').withConverter(userConverter));
    const usersSnapshot = await getDocs(usersQuery);
    return usersSnapshot.docs.map(doc => enhanceUser(doc.data()));
  } catch (error) {
    console.error('Failed to get all users:', error);
    return [];
  }
};

export const getUserById = async (id: string): Promise<User | undefined> => {
  try {
    if (!id) return undefined;
    const userDoc = await getDoc(doc(db, 'users', id).withConverter(userConverter));
    if (userDoc.exists()) {
      return userDoc.data();
    }
    return undefined;
  } catch (error) {
    console.error('Error getting user by ID:', error);
    return undefined;
  }
};

// Note: getUserByUsername function removed as username is no longer used

export const addUser = async (user: User): Promise<void> => {
  try {
    // Ensure password is provided
    if (!user.password) {
      throw new Error('Password is required');
    }

    // Hash the password before storing
    const hashedPassword = await hashPassword(user.password);
    const userWithHashedPassword = {
      ...user,
      password: hashedPassword
    };

    // Add user to Firestore
    const userRef = doc(db, 'users', user.id);
    await setDoc(userRef, userWithHashedPassword);
    
    // Track the activity with real IP and location data
    await logUserActivity(
      'system',
      'System',
      'create',
      user.id,
      user.fullName,
      {
        changedFields: ['fullName', 'email'],
        newValues: {
          fullName: user.fullName,
          email: user.email
        }
      }
    );
  } catch (error) {
    console.error('Failed to add user:', error);
    throw error;
  }
};

export const updateUser = async (user: User): Promise<void> => {
  try {
    const userRef = doc(db, 'users', user.id);
    const currentUser = await getDoc(userRef);
    
    if (!currentUser.exists()) {
      throw new Error('User not found');
    }

    const currentData = currentUser.data() as User;
    let updatedUser = { ...user };

    // If password is being updated, hash it
    if (user.password && user.password !== currentData.password) {
      updatedUser.password = await hashPassword(user.password);
    }

    await updateDoc(userRef, updatedUser);
    
    // Track the activity asynchronously to avoid blocking the UI
    const changedFields = Object.keys(user).filter(key => user[key as keyof User] !== currentData[key as keyof User]);
    
    // Fire and forget - don't await this to avoid delays
    logUserActivity(
      'system',
      'System',
      'update',
      user.id,
      user.fullName,
      {
        changedFields,
        newValues: user
      }
    ).catch(error => {
      console.error('Failed to log user activity (non-blocking):', error);
    });
  } catch (error) {
    console.error('Failed to update user:', error);
    throw error;
  }
};

export const removeUser = async (userId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'users', userId));
  } catch (error) {
    console.error('Failed to remove user:', error);
    throw error;
  }
};

export const authenticateUser = async (email: string, password: string): Promise<User | null> => {
  try {
    // Find user by email instead of username
    const usersQuery = query(
      collection(db, 'users').withConverter(userConverter),
      where('email', '==', email)
    );
    const usersSnapshot = await getDocs(usersQuery);
    const user = usersSnapshot.empty ? null : enhanceUser(usersSnapshot.docs[0].data());
    if (!user || !user.password) return null;
    
    const isValidPassword = await verifyPassword(password, user.password);
    return isValidPassword ? user : null;
  } catch (error) {
    console.error('Failed to authenticate user:', error);
    return null;
  }
};

export const getUsersByRole = async (isAdmin?: boolean, isSuperAdmin?: boolean): Promise<User[]> => {
  try {
    const usersRef = collection(db, 'users').withConverter(userConverter);
    let queryConstraints: any[] = [];
    
    if (isAdmin !== undefined) {
      queryConstraints.push(where('isAdmin', '==', isAdmin));
    }
    
    if (isSuperAdmin !== undefined) {
      queryConstraints.push(where('isSuperAdmin', '==', isSuperAdmin));
    }
    
    const usersQuery = queryConstraints.length > 0 ? query(usersRef, ...queryConstraints) : usersRef;
    const usersSnapshot = await getDocs(usersQuery);
    return usersSnapshot.docs.map(doc => enhanceUser(doc.data()));
  } catch (error) {
    console.error('Failed to get users by role:', error);
    return [];
  }
};

export const searchUsers = async (searchTerm: string): Promise<User[]> => {
  try {
    const usersQuery = query(
      collection(db, 'users').withConverter(userConverter),
      where('fullName', '>=', searchTerm),
      where('fullName', '<=', searchTerm + '\uf8ff')
    );
    const usersSnapshot = await getDocs(usersQuery);
    return usersSnapshot.docs.map(doc => enhanceUser(doc.data()));
  } catch (error) {
    console.error('Failed to search users:', error);
    return [];
  }
};

export const updateNotificationCount = async (userId: string, count: number): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { notificationCount: count });
  } catch (error) {
    console.error('Failed to update notification count:', error);
    throw error;
  }
};

export const setUserSuspended = async (userId: string, suspended: boolean): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { suspended });
  } catch (error) {
    console.error('Failed to set user suspended status:', error);
    throw error;
  }
};

export const setUserProfilePicture = async (userId: string, imageUrl: string, altText?: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      profilePictureUrl: imageUrl,
      profilePictureAltText: altText || `Profile picture for user ${userId}`,
      profilePictureUploadedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to set user profile picture:', error);
    throw error;
  }
};

export const removeUserProfilePicture = async (userId: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      profilePictureUrl: null,
      profilePictureAltText: null,
      profilePictureUploadedAt: null
    });
  } catch (error) {
    console.error('Failed to remove user profile picture:', error);
    throw error;
  }
};

export const hasProfilePicture = (userId: string): boolean => {
  const user = USERS.find(u => u.id === userId);
  return !!user?.profilePictureUrl;
};

export const getUsersWithProfilePictures = async (): Promise<User[]> => {
  try {
    const usersRef = collection(db, 'users').withConverter(userConverter);
    const usersQuery = query(usersRef, where('profilePictureUrl', '!=', null));
    const usersSnapshot = await getDocs(usersQuery);
    return usersSnapshot.docs.map(doc => enhanceUser(doc.data()));
  } catch (error) {
    console.error('Failed to get users with profile pictures:', error);
    return [];
  }
};

export const getUsersWithoutProfilePictures = async (): Promise<User[]> => {
  try {
    const usersRef = collection(db, 'users').withConverter(userConverter);
    const usersQuery = query(usersRef, where('profilePictureUrl', '==', null));
    const usersSnapshot = await getDocs(usersQuery);
    return usersSnapshot.docs.map(doc => enhanceUser(doc.data()));
  } catch (error) {
    console.error('Failed to get users without profile pictures:', error);
    return [];
  }
};

export const updateUserCountry = async (userId: string, country: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { country });
  } catch (error) {
    console.error('Failed to update user country:', error);
    throw error;
  }
};

export const setUserSuperAdmin = async (userId: string, isSuperAdmin: boolean): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { isSuperAdmin });
  } catch (error) {
    console.error('Failed to set user super admin status:', error);
    throw error;
  }
};

export const resetToDefaults = async (): Promise<void> => {
  try {
    // Delete all existing users
    const batch = writeBatch(db);
    const usersSnapshot = await getDocs(collection(db, 'users'));
    usersSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    await batch.commit();
    
    // Initialize with defaults
    await initializeUserDatabase();
  } catch (error) {
    console.error('Failed to reset to defaults:', error);
    throw error;
  }
};

// Enhanced profile picture functions with processing capabilities
export const uploadAndProcessUserProfilePicture = async (
  userId: string,
  imageSource: File | string,
  processingOptions?: ProfilePictureProcessingOptions
): Promise<void> => {
  // Simulate async processing
  return new Promise((resolve, reject) => {
    try {
      let imageUrl: string;
      
      if (typeof imageSource === 'string') {
        imageUrl = imageSource;
      } else {
        // For File objects, create a data URL (in a real app, you'd upload to a server)
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          setUserProfilePicture(userId, result);
          resolve();
        };
        reader.onerror = () => reject(new Error('Failed to read image file'));
        reader.readAsDataURL(imageSource);
        return;
      }
      
      setUserProfilePicture(userId, imageUrl);
      resolve();
    } catch (error) {
      reject(error);
    }
  });
};

export const uploadAndProcessMultipleUserProfilePictures = async (
  userImages: Array<{
    userId: string;
    imageSource: File | string;
    processingOptions?: ProfilePictureProcessingOptions;
  }>
): Promise<void> => {
  // Process all images in parallel
  const promises = userImages.map(({ userId, imageSource, processingOptions }) =>
    uploadAndProcessUserProfilePicture(userId, imageSource, processingOptions)
  );
  
  await Promise.all(promises);
};

export const addUserWithProcessedProfilePicture = async (
  userData: Omit<BaseUser, 'id'>,
  imageSource: File | string,
  processingOptions?: ProfilePictureProcessingOptions,
  userId?: string
): Promise<User> => {
  const id = userId || Math.random().toString(36).substr(2, 9);
  const user: User = {
    ...userData,
    id
  } as User;
  
  addUser(user);
  await uploadAndProcessUserProfilePicture(id, imageSource, processingOptions);
  
  const createdUser = await getUserById(id);
  if (!createdUser) {
    throw new Error('Failed to create user');
  }
  
  return createdUser;
};

export const updateUserWithProcessedProfilePicture = async (
  user: BaseUser,
  imageSource?: File | string,
  processingOptions?: ProfilePictureProcessingOptions,
  removeExistingPicture?: boolean
): Promise<void> => {
  if (!user.id) {
    throw new Error('User ID is required for updates');
  }
  
  const fullUser: User = {
    ...user,
    id: user.id
  } as User;
  
  await updateUser(fullUser);
  
  if (removeExistingPicture) {
    removeUserProfilePicture(user.id);
  }
  
  if (imageSource) {
    await uploadAndProcessUserProfilePicture(user.id, imageSource, processingOptions);
  }
};

// User statistics function
export const getUserStatistics = async () => {
  const allUsers = await getAllUsers();
  const withPictures = await getUsersWithProfilePictures();
  const withoutPictures = await getUsersWithoutProfilePictures();
  const admins = await getUsersByRole(true);
  const superAdmins = await getUsersByRole(undefined, true);
  
  return {
    total: allUsers.length,
    withProfilePictures: withPictures.length,
    withoutProfilePictures: withoutPictures.length,
    admins: admins.length,
    superAdmins: superAdmins.length,
    suspended: allUsers.filter(u => u.suspended).length
  };
};

// Enhanced addUser function with profile picture support
export const addUserEnhanced = (user: BaseUser, profilePictureUrl?: string): void => {
  const userId = user.id || Math.random().toString(36).substr(2, 9);
  const fullUser: User = {
    ...user,
    id: userId
  } as User;
  
  addUser(fullUser);
  
  if (profilePictureUrl) {
    setUserProfilePicture(userId, profilePictureUrl);
  }
};

// Enhanced updateUser function for BaseUser type
export const updateUserEnhanced = (user: BaseUser): void => {
  if (!user.id) {
    throw new Error('User ID is required for updates');
  }
  const fullUser: User = {
    ...user,
    id: user.id
  } as User;
  updateUser(fullUser);
};

// Helper function to fix Super Admin status for debugging
export const fixHavarSuperAdmin = (): void => {
  const havar = USERS.find(user => user.fullName === 'Håvar Bauck');
  if (havar) {
    setUserSuperAdmin(havar.id, true);
  }
};

// Note: updateUserType function removed as userType, companyName, and companyRegNo are no longer used

// Reset users to defaults
export const resetUsersToDefaults = async (): Promise<void> => {
  try {
    // Delete all existing users
    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersRef);
    const deletePromises = usersSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
    
    // Add default users
    const batch = writeBatch(db);
    DEFAULT_USERS.forEach(user => {
      const docRef = doc(db, 'users', user.id);
      batch.set(docRef, user);
    });
    await batch.commit();
    
    // Update local state
    USERS = [...DEFAULT_USERS];
    

  } catch (error) {
    console.error('Failed to reset user database:', error);
    throw error;
  }
};

// Get user by Firebase Auth ID (for lookup purposes only - NEVER use Firebase Auth ID for identification)
export const getUserByFirebaseUid = async (firebaseUid: string): Promise<User | undefined> => {
  try {
    if (!firebaseUid) return undefined;
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('firebaseUid', '==', firebaseUid));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      // Only return if the user has a numeric ID
      if (/^\d+$/.test(userDoc.id)) {
        return {
          ...userDoc.data(),
          id: userDoc.id
        } as User;
      }
    }
    return undefined;
  } catch (error) {
    console.error('Error getting user by Firebase UID:', error);
    return undefined;
  }
}; 